import { Node, Edge, MarkerType } from 'reactflow';
import { isValidConnection, getHandleById, mapLegacyHandleId } from '../workflowDefinitions';
import { WorkflowNodeType, ConnectionType, WorkflowCanvasMode } from '../types';
import { logNodePosition, validatePosition } from '../../../utils/positionDebugUtils';

/**
 * Creates a phase node with the specified properties
 * Follows the exact structure used in the existing codebase
 */
export function createPhaseNode(
  type: string,
  label: string,
  position: { x: number, y: number },
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void,
  onToggleExpand?: (nodeId: string, isExpanded?: boolean) => void
): Node {
  const newNodeId = `phase-${Date.now()}`;
  
  // Ensure position is valid
  const finalPosition = position && typeof position.x === 'number' && typeof position.y === 'number'
    ? position
    : { x: 100 + Math.random() * 500, y: 100 + Math.random() * 500 };
  
  // If label is empty or matches the type, use auto label
  const isAutoLabel = !label || label === type;
  
  // Convert type to title case for auto label
  const autoLabel = type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return {
    id: newNodeId,
    type: 'phase',
    position,
    data: {
      id: newNodeId,
      mode,
      label: isAutoLabel ? autoLabel : label,
      type,
      status: 'not_started',
      isAutoLabel: isAutoLabel,
      expanded: false, // Start collapsed by default
      config: {
        personas: [],
        tools: []
      },
      darkMode: darkMode,
      onUpdate: (nodeId: string, updates: any) => {
        console.log(`onUpdate called for node ${nodeId} with updates:`, updates);
        if (onWorkflowChange) {
          console.log(`Notifying about change with type: update_phase`);
          onWorkflowChange({
            type: 'update_phase',
            phaseId: nodeId,
            updates
          });
        } else {
          console.warn(`No onWorkflowChange callback available`);
        }
      },
      onToggleExpand: (nodeId: string, isExpanded?: boolean) => {
        if (onToggleExpand) {
          onToggleExpand(nodeId, isExpanded);
        }
      }
    }
  };
}

/**
 * Creates a tool node with the specified properties
 * Follows the exact structure used in the existing codebase
 */
export function createToolNode(
  position: { x: number, y: number },
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void
): Node {
  const newNodeId = `tool-${Date.now()}`;
  
  // Ensure position is valid
  const finalPosition = position && typeof position.x === 'number' && typeof position.y === 'number'
    ? position
    : { x: 100 + Math.random() * 500, y: 100 + Math.random() * 300 };
  
  return {
    id: newNodeId,
    type: 'tool',
    position,
    data: {
      id: newNodeId,
      mode,
      isConnectable: true,
      darkMode: darkMode,
      onUpdate: (nodeId: string, updates: any) => {
        console.log(`onUpdate called for tool node ${nodeId} with updates:`, updates);
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'update_tool',
            toolNodeId: nodeId,
            updates
          });
        }
      }
    }
  };
}

/**
 * Creates a persona node with the specified properties
 * Follows the exact structure used in the existing codebase
 */
export function createPersonaNode(
  position: { x: number, y: number },
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void
): Node {
  const newNodeId = `persona-${Date.now()}`;
  
  // Ensure position is valid
  const finalPosition = position && typeof position.x === 'number' && typeof position.y === 'number'
    ? position
    : { x: 100 + Math.random() * 500, y: 100 + Math.random() * 300 };
  
  return {
    id: newNodeId,
    type: 'persona',
    position,
    data: {
      id: newNodeId,
      mode,
      isConnectable: true,
      darkMode: darkMode,
      onUpdate: (nodeId: string, updates: any) => {
        console.log(`onUpdate called for persona node ${nodeId} with updates:`, updates);
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'update_persona',
            personaNodeId: nodeId,
            updates
          });
        }
      }
    }
  };
}

/**
 * Creates an artifact node with the specified properties
 * Follows the exact structure used in the existing codebase
 */
export function createArtifactNode(
  position: { x: number, y: number },
  isOutput: boolean,
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void
): Node {
  const newNodeId = `artifact-${Date.now()}`;
  
  // Ensure position is valid
  const finalPosition = position && typeof position.x === 'number' && typeof position.y === 'number'
    ? position
    : { x: 100 + Math.random() * 500, y: 100 + Math.random() * 300 };
  
  return {
    id: newNodeId,
    type: 'artifact',
    position,
    data: {
      id: newNodeId,
      mode,
      isConnectable: true,
      darkMode: darkMode,
      isOutput: isOutput,
      selectedArtifacts: [],
      onUpdate: (nodeId: string, updates: any) => {
        console.log(`onUpdate called for artifact node ${nodeId} with updates:`, updates);
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'update_artifact',
            artifactNodeId: nodeId,
            updates
          });
        }
      }
    }
  };
}

/**
 * Creates a connected node in the specified direction from the source node
 * Follows the exact structure and logic used in the existing codebase
 */
export function createConnectedNode(
  sourceNodeId: string,
  direction: 'right' | 'bottom' | 'left' | 'top',
  nodes: Node[],
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void,
  onToggleExpand?: (nodeId: string, isExpanded?: boolean) => void
): { newNode: Node, newEdge: Edge } | null {
  const sourceNode = nodes.find(node => node.id === sourceNodeId);
  if (!sourceNode) {
    console.error(`Source node ${sourceNodeId} not found`);
    return null;
  }

  // Calculate position for new node
  const spacing = 150;
  const position = { 
    x: sourceNode.position.x,
    y: sourceNode.position.y
  };

  switch (direction) {
    case 'right':
      position.x += spacing;
      break;
    case 'bottom':
      position.y += spacing;
      break;
    case 'left':
      position.x -= spacing;
      break;
    case 'top':
      position.y -= spacing;
      break;
  }

  // Create new node
  const type = 'default';
  const autoLabel = 'Default';
  
  const newNode = createPhaseNode(
    type,
    autoLabel,
    position,
    mode,
    darkMode,
    onWorkflowChange,
    onToggleExpand
  );

  // Determine source and target nodes based on direction
  const source = direction === 'left' || direction === 'top' ? newNode.id : sourceNodeId;
  const target = direction === 'left' || direction === 'top' ? sourceNodeId : newNode.id;

  // Determine source and target handles based on direction
  let sourceHandle, targetHandle;

  if (direction === 'right') {
    sourceHandle = 'output';
    targetHandle = 'input';
  } else if (direction === 'left') {
    sourceHandle = 'output';
    targetHandle = 'input';
  } else if (direction === 'bottom') {
    sourceHandle = 'artifacts_out';
    targetHandle = 'artifacts_in';
  } else if (direction === 'top') {
    sourceHandle = 'artifacts_out';
    targetHandle = 'artifacts_in';
  }

  // Determine dependency type based on direction
  const dependencyType = direction === 'top' || direction === 'bottom' ? 'enhances' : 'requires';

  // Create the edge using the same pattern as in the existing code
  const newEdge: Edge = {
    id: `e-${source}-${target}`,
    source,
    target,
    sourceHandle,
    targetHandle,
    type: 'smoothstep',
    data: { type: dependencyType },
    style: {
      stroke: dependencyType === 'requires' ? '#ff0000' :
              dependencyType === 'enhances' ? '#00aa00' :
              dependencyType === 'blocks' ? '#aa0000' : '#888888',
      strokeWidth: 2
    },
    markerEnd: {
      type: MarkerType.ArrowClosed
    }
  };

  return { newNode, newEdge };
}

/**
 * Creates a dependency edge between two nodes
 * Follows the exact structure used in the existing codebase
 */
export function createDependencyEdge(
  edgeId: string,
  source: string,
  target: string,
  sourceHandle: string,
  targetHandle: string,
  dependencyType: string
): Edge {
  // Find the connection rule for this edge
  const connectionRule = isValidConnection(
    WorkflowNodeType.PHASE, // Assuming phase-to-phase connection
    sourceHandle,
    WorkflowNodeType.PHASE,
    targetHandle
  );
  
  return {
    id: edgeId,
    source,
    target,
    sourceHandle,
    targetHandle,
    type: 'smoothstep',
    data: { 
      type: connectionRule?.connectionType || dependencyType 
    },
    style: connectionRule?.style || {
      stroke: dependencyType === 'requires' ? '#ff0000' :
              dependencyType === 'enhances' ? '#00aa00' :
              dependencyType === 'blocks' ? '#aa0000' : '#888888',
      strokeWidth: 2
    },
    markerEnd: {
      type: MarkerType.ArrowClosed
    }
  };
}

/**
 * Creates edges from workflow connections with curved splines
 * Enhanced with better styling and curved connectors
 */
export function createEdgesFromConnections(
  connections: Array<{
    source: string;
    target: string;
    sourceHandle: string;
    targetHandle: string;
    dependencyType?: string;
  }>,
  nodes: Node[],
  criticalPath: string[] = []
): Edge[] {
  // Create a map of node IDs to node types for quick lookup
  const nodeTypeMap = new Map<string, WorkflowNodeType>();
  const nodeIdSet = new Set<string>();
  
  nodes.forEach(node => {
    nodeTypeMap.set(node.id, node.type as WorkflowNodeType);
    nodeIdSet.add(node.id);
  });
  
  // Transform connections into edges
  return connections.map((conn, index) => {
    // Log each connection's handle IDs
    console.log(`Creating edge from ${conn.source} to ${conn.target} with sourceHandle=${conn.sourceHandle}, targetHandle=${conn.targetHandle}`);

    // Validate that source and target nodes exist
    if (!nodeIdSet.has(conn.source)) {
      console.warn(`Cannot create edge: source node not found for connection ${conn.source} -> ${conn.target}`);
      return null;
    }
    
    if (!nodeIdSet.has(conn.target)) {
      console.warn(`Cannot create edge: target node not found for connection ${conn.source} -> ${conn.target}`);
      return null;
    }

    // Find source and target nodes to determine their types
    const sourceType = nodeTypeMap.get(conn.source);
    const targetType = nodeTypeMap.get(conn.target);
    
    if (!sourceType || !targetType) {
      console.warn(`Cannot create edge: source or target node type not determined for connection ${conn.source} -> ${conn.target}`);
      return null;
    }
    
    // Map handle IDs using the proper mapping function
    const sourceHandle = mapLegacyHandleId(
      sourceType, 
      conn.sourceHandle
    );
    
    const targetHandle = mapLegacyHandleId(
      targetType, 
      conn.targetHandle
    );
    
    // Find the connection rule for this edge
    const connectionRule = isValidConnection(
      sourceType,
      sourceHandle,
      targetType,
      targetHandle
    );
    
    // Create a unique ID that includes the handle IDs to avoid duplicates
    const edgeId = `e-${conn.source}-${conn.target}-${sourceHandle}-${targetHandle}-${index}`;

    const isCritical = criticalPath.includes(conn.source) && criticalPath.includes(conn.target);
    const dependencyType = connectionRule?.connectionType || conn.dependencyType || 'default';

    // Get source and target node labels for tooltips
    const sourceNode = nodes.find(n => n.id === conn.source);
    const targetNode = nodes.find(n => n.id === conn.target);

    return {
      id: edgeId,
      source: conn.source,
      target: conn.target,
      sourceHandle: sourceHandle,
      targetHandle: targetHandle,
      type: 'curved', // Use our custom curved edge
      animated: isCritical, // Animate critical path edges
      data: {
        type: dependencyType,
        label: getEdgeLabel(dependencyType),
        sourceNodeId: conn.source,
        targetNodeId: conn.target,
        sourceNodeLabel: sourceNode?.data?.label || sourceNode?.data?.toolName || sourceNode?.data?.personaName || conn.source,
        targetNodeLabel: targetNode?.data?.label || targetNode?.data?.toolName || targetNode?.data?.personaName || conn.target
      },
      style: {
        stroke: getEdgeColor(dependencyType, isCritical),
        strokeWidth: isCritical ? 3 : 2,
        strokeDasharray: getEdgePattern(dependencyType),
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: getEdgeColor(dependencyType, isCritical),
        width: 20,
        height: 20,
      }
    };
  }).filter(Boolean);
}

/**
 * Get edge color based on dependency type and critical path status
 */
function getEdgeColor(dependencyType: string, isCritical: boolean = false): string {
  if (isCritical) {
    return '#ef4444'; // Red for critical path
  }

  switch (dependencyType) {
    case 'flow':
    case 'requires':
      return '#f59e0b'; // Golden/amber for execution flow (like motherboard traces)
    case 'tool_assignment':
    case 'persona_assignment':
      return '#10b981'; // Green for tools and personas
    case 'input_artifact':
    case 'output_artifact':
      return '#8b5cf6'; // Purple for artifact flows
    case 'enhances':
      return '#06b6d4'; // Cyan for enhancements
    case 'blocks':
      return '#f97316'; // Orange for blocking dependencies
    default:
      return '#f59e0b'; // Golden for default (execution flow)
  }
}

/**
 * Get edge pattern (dash array) based on dependency type
 */
function getEdgePattern(dependencyType: string): string | undefined {
  switch (dependencyType) {
    case 'tool_assignment':
    case 'persona_assignment':
      return '8,4'; // Dashed line for assignments
    case 'input_artifact':
    case 'output_artifact':
      return '4,4'; // Dotted line for artifacts
    case 'enhances':
      return '12,4,4,4'; // Dash-dot for enhancements
    case 'blocks':
      return '8,8'; // Long dashes for blocking
    default:
      return undefined; // Solid line for flow and default
  }
}

/**
 * Get edge label based on dependency type
 */
function getEdgeLabel(dependencyType: string): string | undefined {
  switch (dependencyType) {
    case 'tool_assignment':
      return '🔧';
    case 'persona_assignment':
      return '👤';
    case 'input_artifact':
      return '📥';
    case 'output_artifact':
      return '📤';
    case 'enhances':
      return '✨';
    case 'blocks':
      return '🚫';
    case 'flow':
    case 'requires':
      return '→';
    case 'default':
      return '→';
    default:
      return '→'; // Always show some icon
  }
}

/**
 * Converts ReactFlow edges to workflow connections
 * Follows the exact structure used in the existing codebase
 */
export function createConnectionsFromEdges(edges: Edge[]): Array<{
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  dependencyType: string;
}> {
  return edges.map(edge => {
    // Log the edge being converted
    console.log(`Converting edge ${edge.id} with sourceHandle=${edge.sourceHandle}, targetHandle=${edge.targetHandle}`);
    
    return {
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      dependencyType: edge.data?.type || 'default'
    };
  });
}

/**
 * Validates workflow edges to ensure they are properly connected
 * This is a helper function that doesn't modify the workflow structure
 */
export function validateWorkflowEdges(
  nodes: Node[],
  edges: Edge[]
): { valid: boolean, issues: string[] } {
  const issues: string[] = [];
  
  // Create a map of node IDs for quick lookup
  const nodeMap = new Map<string, Node>();
  nodes.forEach(node => {
    nodeMap.set(node.id, node);
  });
  
  // Check that all edge sources and targets exist
  edges.forEach(edge => {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);
    
    if (!sourceNode) {
      issues.push(`Edge ${edge.id} has a source node (${edge.source}) that doesn't exist`);
    }
    
    if (!targetNode) {
      issues.push(`Edge ${edge.id} has a target node (${edge.target}) that doesn't exist`);
    }
  });
  
  return { 
    valid: issues.length === 0,
    issues 
  };
}

/**
 * Ensures edge IDs are consistent and unique
 * This is a helper function that doesn't change the fundamental structure
 */
export function ensureUniqueEdgeIds(edges: Edge[]): Edge[] {
  // Create a map to track edges by their connection
  const connectionMap = new Map<string, Edge>();
  
  edges.forEach(edge => {
    // Create a key that uniquely identifies this connection
    const connectionKey = `${edge.source}-${edge.target}-${edge.sourceHandle}-${edge.targetHandle}`;
    
    // If we already have an edge for this connection, keep the one with the most data
    if (connectionMap.has(connectionKey)) {
      const existingEdge = connectionMap.get(connectionKey);
      
      // Prefer the edge with data
      if (!existingEdge.data && edge.data) {
        connectionMap.set(connectionKey, edge);
      }
    } else {
      // Add the edge to the map
      connectionMap.set(connectionKey, edge);
    }
  });
  
  // Return the unique edges
  return Array.from(connectionMap.values());
}
